/**
 * Template Panel
 * Displays template selection grid with live layer previews
 */

import React, { useCallback, useMemo, useEffect } from 'react';
import { Layers } from 'lucide-react';
import { useEditorStore, useTemplateStore } from '@/stores';
import { getAllTemplates } from '@/templates';
import type { Template } from '@/types/template';
import { getCanvasBackgroundCSS } from '@/types/template';
import { LayerRenderer } from '@/components/canvas/LayerRenderer';

interface TemplateCardProps {
  template: Template;
  isSelected: boolean;
  formatWidth: number;
  formatHeight: number;
  onSelect: () => void;
}

function TemplateCard({
  template,
  isSelected,
  formatWidth,
  formatHeight,
  onSelect,
}: TemplateCardProps) {
  const previewWidth = 180;
  const previewHeight = (previewWidth * formatHeight) / formatWidth;
  const bgStyle = getCanvasBackgroundCSS(template.canvas.background);

  // Calculate scale for the preview
  const scale = previewWidth / template.canvas.baseWidth;

  // Create a format object for the preview
  const previewFormat = useMemo(() => ({
    id: 'preview',
    name: 'Preview',
    width: template.canvas.baseWidth,
    height: template.canvas.baseHeight,
    provider: 'custom' as const,
    platforms: '',
  }), [template.canvas.baseWidth, template.canvas.baseHeight]);

  // Default variable values for preview
  const previewVariables = useMemo(() => {
    const values: Record<string, string> = {};
    template.variables.forEach(v => {
      values[v.id] = v.defaultValue;
    });
    return values;
  }, [template.variables]);

  return (
    <button
      onClick={onSelect}
      className={`text-left rounded-xl p-3 transition-all ${
        isSelected
          ? 'ring-2 ring-primary bg-primary/10'
          : 'bg-white/[0.02] hover:bg-white/5 border border-white/5'
      }`}
    >
      {/* Preview with actual layer rendering */}
      <div
        className="rounded-lg overflow-hidden mb-3 relative"
        style={{
          width: previewWidth,
          height: previewHeight,
          background: bgStyle,
        }}
      >
        {/* Scaled layer preview */}
        <div
          style={{
            width: template.canvas.baseWidth,
            height: template.canvas.baseHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        >
          <LayerRenderer
            layers={template.layers}
            format={previewFormat}
            variableValues={previewVariables}
            selectedLayerIds={[]}
            hoveredLayerId={null}
          />
        </div>

        {/* Layer count badge */}
        <div className="absolute bottom-2 right-2 z-10">
          <div className="px-1.5 py-0.5 bg-black/50 rounded text-[9px] text-white">
            {template.layers.length} layers
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm text-fg">{template.metadata.name}</h4>
            {!template.metadata.isBuiltin && (
              <span className="text-[9px] px-1 py-0.5 bg-accent-glow/20 text-accent-glow rounded">
                Custom
              </span>
            )}
          </div>
          <p className="text-xs text-fg-subtle mt-0.5 line-clamp-1">
            {template.metadata.description}
          </p>
        </div>
        {isSelected && (
          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-none">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
      </div>
    </button>
  );
}

export function TemplatePanel() {
  const { selectedFormat } = useEditorStore();
  const { currentTemplate, loadTemplate } = useTemplateStore();

  // Get all templates
  const templates = useMemo(() => getAllTemplates(), []);

  // Auto-load first template if none selected
  useEffect(() => {
    if (!currentTemplate && templates.length > 0) {
      loadTemplate(templates[0]);
    }
  }, [currentTemplate, templates, loadTemplate]);

  // Handle template selection
  const handleSelectTemplate = useCallback(
    (template: Template) => {
      loadTemplate(template);
    },
    [loadTemplate]
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="label mb-0">Templates</h3>
      </div>

      {/* Templates grid */}
      <div className="grid grid-cols-3 gap-4">
        {templates.map((template) => (
          <TemplateCard
            key={template.metadata.id}
            template={template}
            isSelected={currentTemplate?.metadata.id === template.metadata.id}
            formatWidth={selectedFormat.width}
            formatHeight={selectedFormat.height}
            onSelect={() => handleSelectTemplate(template)}
          />
        ))}

      </div>

      {/* Help text */}
      <div className="mt-4 p-3 bg-white/[0.02] rounded-lg">
        <div className="flex items-start gap-2">
          <Layers size={14} className="text-fg-muted flex-none mt-0.5" />
          <p className="text-xs text-fg-muted">
            Templates are layer-based designs you can customize. Select layers on the canvas
            or use the Layers panel to edit visibility, order, and properties.
          </p>
        </div>
      </div>
    </div>
  );
}
